import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import React from 'react'

import {
  commitDailyImport,
  fetchDailyImportPreview,
  fetchDailyImportTabs
} from 'api/apartmentProcurement'

// The team's "Daily (Dept to fill)" sheet — prefilled; editable if it moves.
const DEFAULT_URL =
  'https://docs.google.com/spreadsheets/d/1lr_l1Kw47nMud3tfjBMbhMoVE1CzupnoWy3i1zNMcS8/edit'

const errMsg = (e) =>
  (e && e.response && e.response.data && e.response.data.detail) ||
  (e && e.message) ||
  'Something went wrong.'

const useStyles = makeStyles((theme) => ({
  tabs: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    maxHeight: 160,
    overflowY: 'auto',
    margin: theme.spacing(1, 0)
  },
  tab: {
    display: 'inline-flex',
    alignItems: 'center',
    border: '1px solid #e0ddd6',
    borderRadius: 6,
    padding: '2px 8px 2px 2px',
    fontSize: 13
  },
  preview: {
    marginTop: theme.spacing(1),
    background: '#faf9f6',
    border: '1px solid #eee',
    borderRadius: 6,
    padding: theme.spacing(1),
    maxHeight: 200,
    overflowY: 'auto',
    fontSize: 12.5
  },
  prow: { display: 'flex', gap: 10, padding: '2px 0', borderBottom: '1px solid #f0efe9' }
}))

const DailyImportDialog = ({ onClose, onImported }) => {
  const classes = useStyles()
  const [url, setUrl] = React.useState(DEFAULT_URL)
  const [tabs, setTabs] = React.useState(null)
  const [sel, setSel] = React.useState(() => new Set())
  const [preview, setPreview] = React.useState(null)
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState('')
  const [done, setDone] = React.useState('')

  const loadTabs = async () => {
    setBusy(true)
    setError('')
    setTabs(null)
    setPreview(null)
    try {
      const d = await fetchDailyImportTabs(url)
      setTabs(d.tabs || [])
    } catch (e) {
      setError(errMsg(e))
    } finally {
      setBusy(false)
    }
  }

  const doPreview = async (tab) => {
    setBusy(true)
    setError('')
    try {
      const d = await fetchDailyImportPreview(url, tab)
      setPreview(d)
    } catch (e) {
      setError(errMsg(e))
    } finally {
      setBusy(false)
    }
  }

  const toggle = (tab) =>
    setSel((prev) => {
      const next = new Set(prev)
      if (next.has(tab)) next.delete(tab)
      else next.add(tab)
      return next
    })

  const commit = async () => {
    setBusy(true)
    setError('')
    try {
      const d = await commitDailyImport(url, [...sel])
      const total = Object.values(d.imported || {}).reduce((a, b) => a + b, 0)
      setDone(`Imported ${total} line${total === 1 ? '' : 's'} across ${sel.size} date${sel.size === 1 ? '' : 's'}.`)
      onImported()
    } catch (e) {
      setError(errMsg(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Pull from the Daily (Dept to fill) sheet</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Pick the date tabs to pull. Re-pulling a date replaces its previously
          imported lines; your manual entries are left alone. The sheet must be
          shared with the service account.
        </Typography>
        <Box display="flex" style={{ gap: 8 }} alignItems="center">
          <TextField
            fullWidth
            size="small"
            label="Google Sheet URL"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <Button variant="outlined" size="small" disabled={busy} onClick={loadTabs}>
            Load dates
          </Button>
        </Box>

        {busy && (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={22} />
          </Box>
        )}

        {tabs && !tabs.length && !busy && (
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
            No date tabs (DD-MM-YYYY) found in that sheet.
          </Typography>
        )}

        {tabs && !!tabs.length && (
          <div className={classes.tabs}>
            {tabs.map((t) => (
              <span key={t} className={classes.tab}>
                <Checkbox
                  size="small"
                  checked={sel.has(t)}
                  onChange={() => toggle(t)}
                />
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => doPreview(t)}
                  title="Preview this date">
                  {t}
                </Link>
              </span>
            ))}
          </div>
        )}

        {preview && (
          <div className={classes.preview}>
            <b>{preview.tab}</b> — {preview.count} line{preview.count === 1 ? '' : 's'}
            {(preview.lines || []).slice(0, 12).map((l, i) => (
              <div key={i} className={classes.prow}>
                <span style={{ width: 90 }}>{l.centre_label || '—'}</span>
                <span style={{ flex: 1 }}>{l.item_name}</span>
                <span style={{ width: 70 }}>{l.category || '—'}</span>
                <span style={{ width: 40, textAlign: 'right' }}>{String(l.qty)}</span>
              </div>
            ))}
          </div>
        )}

        {error && (
          <Typography color="error" variant="body2" style={{ marginTop: 8 }}>
            {error}
          </Typography>
        )}
        {done && (
          <Typography variant="body2" style={{ marginTop: 8, color: '#2e7d32' }}>
            {done}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          color="primary"
          variant="contained"
          disableElevation
          disabled={busy || sel.size === 0}
          onClick={commit}>
          Import {sel.size || ''} date{sel.size === 1 ? '' : 's'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DailyImportDialog
